import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { PageIntro } from "../components/PageIntro";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { WHY_FRANCHISE, FRANCHISE_STEPS } from "../lib/data";
import { submitFranchise } from "../lib/api";

const initial = { name: "", email: "", phone: "", pincode: "", city: "", background: "", message: "" };

export default function Franchise() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.pincode) {
      toast.error("Please fill in your name, email, phone and pincode.");
      return;
    }
    setLoading(true);
    try {
      await submitFranchise(form);
      setDone(true);
      setForm(initial);
      toast.success("Application received! We'll be in touch about your territory.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="franchise-page">
      <PageIntro
        label="Franchise · Partner with us"
        titleLines={["Run your own", "microgreens unit", "in your pincode."]}
        subtitle="A compact, low-capital farm business with recurring subscription revenue — exclusive to your neighbourhood territory."
      />

      {/* Why franchise */}
      <section className="px-5 md:px-10 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_FRANCHISE.map((item, i) => (
              <StaggerItem key={i}>
                <div className="h-full bg-brand-card rounded-[1.5rem] p-8 border border-black/5">
                  <span className="font-mono text-sm text-brand-sprout">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-serif text-2xl text-brand-ink leading-tight">{item.title}</h3>
                  <p className="mt-3 text-brand-muted leading-relaxed text-sm">{item.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Who + steps */}
      <section className="px-5 md:px-10 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
              Who we're looking for
            </p>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-brand-ink leading-tight">
              Homemakers, young entrepreneurs, retirees, farmers diversifying.
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed">
              Anyone with a few hours a day, a small dedicated space, and the drive to build a
              customer base in their own community.
            </p>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7 space-y-4">
            {FRANCHISE_STEPS.map((s) => (
              <Reveal key={s.n} delay={0.05}>
                <div className="flex items-start gap-6 border-t border-black/10 pt-5">
                  <span className="font-mono text-sm text-brand-sprout">{s.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl text-brand-ink">{s.title}</h3>
                    <p className="text-brand-muted text-sm mt-1">{s.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="px-5 md:px-10 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto">
          <div className="rounded-[2rem] bg-brand-dark text-brand-bg p-8 md:p-16 relative overflow-hidden">
            <div className="noise-overlay" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-4">
                <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
                  Apply for a franchise
                </p>
                <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">
                  Tell us your pincode.
                </h2>
                <p className="mt-6 text-brand-bg/70 leading-relaxed">
                  Franchise investment details are shared during the application discussion.
                </p>
              </div>

              <div className="md:col-span-7 md:col-start-6">
                {done ? (
                  <div data-testid="franchise-success" className="flex flex-col items-start gap-4 h-full justify-center">
                    <div className="w-14 h-14 rounded-full bg-brand-sprout flex items-center justify-center">
                      <Check size={26} className="text-white" />
                    </div>
                    <h3 className="font-serif text-3xl">Application received.</h3>
                    <p className="text-brand-bg/70 max-w-md">
                      We'll review your territory and reach out shortly. Thank you for your interest
                      in growing with 40X Leaves.
                    </p>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-2 text-sm font-semibold text-brand-sprout hover:underline"
                    >
                      Submit another application
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} data-testid="franchise-form" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="f-name" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Full name *</Label>
                        <Input id="f-name" data-testid="franchise-name" value={form.name} onChange={update("name")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="Your name" />
                      </div>
                      <div>
                        <Label htmlFor="f-phone" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Phone *</Label>
                        <Input id="f-phone" data-testid="franchise-phone" value={form.phone} onChange={update("phone")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="Your phone" />
                      </div>
                      <div>
                        <Label htmlFor="f-email" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Email *</Label>
                        <Input id="f-email" type="email" data-testid="franchise-email" value={form.email} onChange={update("email")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="you@email.com" />
                      </div>
                      <div>
                        <Label htmlFor="f-pincode" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Pincode *</Label>
                        <Input id="f-pincode" data-testid="franchise-pincode" value={form.pincode} onChange={update("pincode")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="e.g. 682001" />
                      </div>
                      <div>
                        <Label htmlFor="f-city" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">City / Town</Label>
                        <Input id="f-city" data-testid="franchise-city" value={form.city} onChange={update("city")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="Your city" />
                      </div>
                      <div>
                        <Label htmlFor="f-bg" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Your background</Label>
                        <Input id="f-bg" data-testid="franchise-background" value={form.background} onChange={update("background")} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout" placeholder="Homemaker, farmer, entrepreneur…" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="f-message" className="text-brand-bg/70 font-mono text-xs uppercase tracking-wider">Anything else?</Label>
                      <Textarea id="f-message" data-testid="franchise-message" value={form.message} onChange={update("message")} rows={3} className="mt-2 bg-transparent border-0 border-b border-white/20 rounded-none px-0 text-brand-bg placeholder:text-brand-bg/30 focus-visible:ring-0 focus-visible:border-brand-sprout resize-none" placeholder="Tell us about your space and why you're interested." />
                    </div>
                    <button
                      type="submit"
                      data-testid="franchise-submit"
                      disabled={loading}
                      className="inline-flex items-center gap-2 rounded-full bg-brand-sprout text-white px-8 py-4 text-sm font-semibold transition-transform duration-300 hover:scale-105 disabled:opacity-60"
                    >
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />}
                      {loading ? "Submitting…" : "Apply for a franchise"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
